// ─── Welcome Page ───────────────────────────────────────────────
// Full-viewport, no sidebar. Two-path fork:
//   1. Connect existing repo → /welcome/connect flow
//   2. Create new repo → existing AddProject + Wizard flow
//
// Design: Minimal, dark, monospace. Matches dashboard aesthetic.
// Renders only when OnboardingContext.showWelcome === true.
import React, { useEffect, useState } from 'react';
import { ArrowRight, GitBranch, Plus, Zap, Shield, FileText } from 'lucide-react';
import { api } from '../api.js';

interface WelcomeProps {
  onConnect: () => void;
  onCreateNew: () => void;
  onSkip?: () => void;
}

export default function Welcome({ onConnect, onCreateNew, onSkip }: WelcomeProps) {
  const [apiVersion, setApiVersion] = useState<string>(__APP_VERSION__);
  useEffect(() => {
    api.health().then((h: { version?: string }) => { if (h?.version) setApiVersion(h.version); }).catch(() => {});
  }, []);

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1000,
      background: 'var(--bg)',
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      fontFamily: 'var(--font-mono)',
      color: 'var(--text)',
      overflow: 'auto',
    }}>
      {/* Header */}
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0,
        padding: '20px 32px',
        display: 'flex', alignItems: 'center', gap: 10,
      }}>
        <div style={{
          width: 28, height: 28, borderRadius: 6,
          background: 'var(--blue)', color: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 13, fontWeight: 700,
        }}>OC</div>
        <span style={{ fontSize: 'var(--text-sm)', fontWeight: 600, letterSpacing: '0.03em' }}>
          OCNexus
        </span>
        <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-dim)', marginLeft: 4 }}>
          v{apiVersion}
        </span>
      </div>

      {/* Main content */}
      <div style={{ maxWidth: 720, width: '100%', padding: '0 32px', textAlign: 'center' }}>
        {/* Headline */}
        <h1 style={{
          fontSize: 28, fontWeight: 700, lineHeight: 1.3,
          marginBottom: 8, fontFamily: 'var(--font-sans, Syne, sans-serif)',
          letterSpacing: '-0.02em',
        }}>
          Ship your first PR in under 10 minutes
        </h1>
        <p style={{
          fontSize: 'var(--text-sm)', color: 'var(--text-dim)',
          lineHeight: 1.6, maxWidth: 480, margin: '0 auto 40px',
        }}>
          OCNexus connects to your repository, learns your codebase, and generates
          production-ready code changes — all through natural language.
        </p>

        {/* Two-path fork */}
        <div style={{
          display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16,
          marginBottom: 48,
        }}>
          {/* Path 1: Connect existing */}
          <button
            onClick={onConnect}
            style={{
              background: 'var(--surface)',
              border: '1px solid var(--border)',
              borderRadius: 10,
              padding: '28px 24px',
              cursor: 'pointer',
              textAlign: 'left',
              transition: 'border-color 0.15s, box-shadow 0.15s',
              display: 'flex', flexDirection: 'column', gap: 12,
              color: 'var(--text)',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.borderColor = 'var(--blue)';
              e.currentTarget.style.boxShadow = '0 0 0 1px var(--blue)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.borderColor = 'var(--border)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            <div style={{
              width: 40, height: 40, borderRadius: 8,
              background: 'rgba(56, 139, 253, 0.1)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <GitBranch size={20} style={{ color: 'var(--blue)' }} />
            </div>
            <div>
              <div style={{ fontSize: 'var(--text-base)', fontWeight: 600, marginBottom: 4 }}>
                Connect existing repo
              </div>
              <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-dim)', lineHeight: 1.5 }}>
                Link a GitHub repository you already have. We'll scan it and set up your pipeline automatically.
              </div>
            </div>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 4,
              fontSize: 'var(--text-xs)', color: 'var(--blue)',
              marginTop: 'auto',
            }}>
              <span>Get started</span>
              <ArrowRight size={12} />
            </div>
          </button>

          {/* Path 2: Create new */}
          <button
            onClick={onCreateNew}
            style={{
              background: 'var(--surface)',
              border: '1px solid var(--border)',
              borderRadius: 10,
              padding: '28px 24px',
              cursor: 'pointer',
              textAlign: 'left',
              transition: 'border-color 0.15s, box-shadow 0.15s',
              display: 'flex', flexDirection: 'column', gap: 12,
              color: 'var(--text)',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.borderColor = 'var(--green)';
              e.currentTarget.style.boxShadow = '0 0 0 1px var(--green)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.borderColor = 'var(--border)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            <div style={{
              width: 40, height: 40, borderRadius: 8,
              background: 'rgba(63, 185, 80, 0.1)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Plus size={20} style={{ color: 'var(--green)' }} />
            </div>
            <div>
              <div style={{ fontSize: 'var(--text-base)', fontWeight: 600, marginBottom: 4 }}>
                Create new project
              </div>
              <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-dim)', lineHeight: 1.5 }}>
                Start from scratch — create a new GitHub repo and configure your full pipeline with our setup wizard.
              </div>
            </div>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 4,
              fontSize: 'var(--text-xs)', color: 'var(--green)',
              marginTop: 'auto',
            }}>
              <span>Create project</span>
              <ArrowRight size={12} />
            </div>
          </button>
        </div>

        {/* What you get */}
        <div style={{
          display: 'flex', gap: 32, justifyContent: 'center',
          fontSize: 'var(--text-xs)', color: 'var(--text-dim)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Zap size={14} style={{ color: 'var(--yellow)' }} />
            <span>AI-powered pipeline</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Shield size={14} style={{ color: 'var(--green)' }} />
            <span>Security scanning</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <FileText size={14} style={{ color: 'var(--blue)' }} />
            <span>Auto documentation</span>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div style={{
        position: 'absolute', bottom: 0, left: 0, right: 0,
        padding: '16px 32px',
        display: 'flex', justifyContent: 'center',
        fontSize: 'var(--text-xs)', color: 'var(--text-dim)',
      }}>
        <span>Already have projects? </span>
        <button
          onClick={() => {
            // Skip to dashboard — navigate directly to overview
            if (onSkip) { onSkip(); } else { window.location.hash = '#overview'; window.location.reload(); }
          }}
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: 'var(--blue)', fontSize: 'var(--text-xs)',
            textDecoration: 'underline', marginLeft: 4,
            fontFamily: 'inherit',
          }}
        >
          Skip to dashboard
        </button>
      </div>
    </div>
  );
}
